import { SourceContext, EmbedderInfo } from '../types';
import {
  ChatMessageAiAnswerSource,
  ChatMessageAiAnswerSourceFileRef,
  FilePayload,
  Partition,
  ResolvedExtension,
} from '../entities';

import { BusinessContext, UnifiedAttachment } from './businessContext';

import type { RJSFSchema, UiSchema } from '@rjsf/utils';

export const ATTACHMENT_MAX_FILE_SIZE = 10 * 1024 * 1024;
export const UPLOADED_ATTACHMENT_MAX_TOTAL_SIZE_PER_MESSAGE = 25 * 1024 * 1024;
export const MAX_TOTAL_ATTACHMENTS_SIZE_PER_CONVERSATION = 45 * 1024 * 1024;
export const UPLOADED_ATTACHMENT_MAX_FILES_PER_MESSAGE = 3;
export const ATTACHMENT_MAX_TOKENS_PER_FILE = 10_000;
export const UPLOADED_ATTACHMENT_MAX_TOKENS_PER_MESSAGE = 20_000;
export const MAX_TOTAL_ATTACHMENT_TOKENS_PER_CONVERSATION = 40_000;

export type ReasoningConfig = {
  effort?: 'none' | 'low' | 'medium' | 'high' | 'xhigh';
};

export type Source = {
  id?: number | string;
  url: string;
  title: string;
  type: string;
  description?: string;
  citations?: string[];
  tags?: string[];
  users?: string[];
  /** Set for uploaded files, which have no url — see `ChatMessageAiAnswerSource`. */
  fileRef?: ChatMessageAiAnswerSourceFileRef;
};

export interface AiLayerAction {
  action_id: string;
  action_prompt?: string;
  no_result_prompt?: string;
  partitions: Array<Array<string> | Pick<Partition, 'dataSourcesIds' | 'uiHidden'>>;

  extra_config: Partial<Record<string, unknown>>;
  user_input_required: boolean;
}

export interface AilLayerChatMessageRequest {
  type: 'new-message';
  chat_id: string;
  message_id: string;
  message?: string;
  attachments?: string[];
  sourceAttachments?: UnifiedAttachment[];
  userAttachmentSize?: number;
  userAttachmentTokens?: number;
  nudgeId?: string;
  background_prompt?: string;
  worknet_context: {
    stage: string;
    org_name: string;
    user?: {
      // TODO: the casing is wrong. should be user_id and user_email, like all other passed values
      userEmail?: string;
      userId?: string;
      verified: boolean;
      jwtClaims?: Record<string, unknown>;
    };
    source: {
      type: string;
      id: string;
    };
    action: {
      id: string;
      extensions: ResolvedExtension[];
      allowLLM: boolean;
      newAiLayer?: boolean;
      traceWebhookUrl?: string;
      disableUiComponents?: boolean;
    };
    wn_headers?: { [key: string]: string };
    device_tracking_id?: string;
  };
}

export interface AiLayerChatRequest extends Omit<AilLayerChatMessageRequest, 'type'> {
  type: 'new-chat';
  source_context?: SourceContext & { resolved?: BusinessContext };
  custom_context?: unknown;
  embedder_info?: EmbedderInfo;
  actions: {
    initial: AiLayerAction;
    iterate: AiLayerAction;
  };
}

export type AiRjsfFormDescriptor = {
  type: 'rjsf';
  data: {
    schema: RJSFSchema;
    ui_schema?: UiSchema;
  };
};

type SlackInterventionStartCommandData = {
  connection_type: 'slack';
  message: string;
  channel_id: string;
  initiator: 'ai';
  sync_prev_messages: false;
  new_thread: true;
};

export type AiCommand = {
  type: 'intervention:start';
  extensionId: string;
  data: SlackInterventionStartCommandData;
};
// | {
//     type: 'slack:message';
//     data: { app_id: string; team_id: string; channel_id: string; thread_ts?: string };
//   }
// | {
//     type: 'internal:intervention';
//     data: { start?: true };
//   };

export interface AiLayerResponse {
  answer: string;
  sources: Source[];
  follow_up_questions?: string[];
  answer_paragraphs_with_citations?: {
    paragraph: string;
    citations: { source_idx: number; citation: string }[];
  }[];
  relevant_knowledge_not_found?: boolean;
  next_route?: { next_route: string; payload?: object };
  form?: AiRjsfFormDescriptor;
  commands?: AiCommand[];
  attachments?: FilePayload[];
  error?: string;
}

export type AiResponseEncodedUiComponents =
  | {
      type: 'wn-buttons-group';
      buttons: {
        label: string;
        prompt: string;
      }[];
    }
  | {
      type: 'wn-video-player';
      videoUrl: string;
      videoPlatform:
        | {
            platform: 'youtube';
            videoId: string;
          }
        | {
            platform: 'vimeo';
            videoId: string;
          }
        | {
            platform: 'loom';
            videoId: string;
          };
      autoPlay: boolean;
    }
  | {
      type: 'wn-image-viewer';
      imageUrl: string;
    }
  | {
      type: 'wn-scheduling';
      platform: 'calendly';
      schedulingUrl: string;
    }
  | {
      type: 'wn-scheduling';
      platform: 'hubspot';
      schedulingUrl: string;
    };

export type ChatEncoding =
  | {
      type: 'sources';
      sources: ChatMessageAiAnswerSource[];
    }
  | {
      type: 'cite';
      id: string | number;
    }
  | {
      type: 'ui-components';
      component: AiResponseEncodedUiComponents;
    };

export const ENCODING_SPECIAL_CHAR = '←';

export type ClientAction =
  | {
      action: 'navigate';
      url: string;
    }
  | {
      action: 'show_alert';
      message: string;
    }
  | {
      action: 'raise_event';
      event_name: string;
    };
